import React, { useState } from 'react';
import { NavLink, useNavigate, Link } from 'react-router-dom';
import {
  LayoutDashboard, ClipboardList, FileText,
  Shield, ShieldCheck, Truck, Menu, X, LogOut, ChevronRight, Globe, ChevronDown, History
} from 'lucide-react';
import useAuth from '../../hooks/useAuth';
import { useLanguage } from '../../contexts/LanguageContext';

const LANGUAGES = { en: 'English', hy: 'Հայերեն', ru: 'Русский' };

const NAV = [
  { to: '/admin-panel/dashboard',     labelKey: 'dashboard',     icon: LayoutDashboard },
  { to: '/admin-panel/registrations', labelKey: 'registrations', icon: ClipboardList   },
  { to: '/admin-panel/invoices',      labelKey: 'invoices',      icon: FileText        },
  { to: '/admin-panel/insurance',     labelKey: 'insurance',     icon: Shield          },
  { to: '/admin-panel/preorders',     labelKey: 'orderedInsurances', icon: ShieldCheck },
  { to: '/admin-panel/vehicles',      labelKey: 'vehicleFleet',  icon: Truck           },
];

export default function AdminLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);   // desktop collapse (md+)
  const [mobileOpen, setMobileOpen]   = useState(false);  // mobile off-canvas drawer (<md)
  const [showLang, setShowLang]       = useState(false);
  const { user, logout }              = useAuth();
  const navigate                      = useNavigate();
  const { language, setLanguage, t }  = useLanguage();

  const fullAdmin = !!user && ['admin', 'superadmin'].includes(user.role) && user.is_staff && user.is_superuser;

  const closeMobile = () => setMobileOpen(false);
  const handleLogout = async () => { await logout(); navigate('/login'); };

  // Collapse (icons-only) applies on DESKTOP only; the mobile drawer is always full width.
  const labelCls = sidebarOpen ? '' : 'md:hidden';
  const iconMr   = sidebarOpen ? 'mr-3' : 'mr-3 md:mr-0';

  const navLinkCls = ({ isActive }) =>
    `group flex items-center px-3 py-3 text-sm font-medium rounded-md transition-colors ${
      isActive ? 'bg-primary-900 text-white' : 'text-primary-100 hover:bg-primary-700'
    }`;

  return (
    <div className="flex h-screen bg-neutral-50 overflow-hidden">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={closeMobile} aria-hidden="true" />
      )}

      {/* Sidebar: off-canvas drawer on mobile, docked/collapsible on desktop */}
      <aside
        className={`fixed md:static inset-y-0 left-0 z-50 flex flex-col bg-primary-800 text-white flex-shrink-0
          w-64 ${sidebarOpen ? 'md:w-64' : 'md:w-16'}
          transform transition-transform duration-200 md:transition-all
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
      >
        {/* Logo + toggle */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-primary-700">
          <Link to="/" className={`flex-shrink-0 ${labelCls}`}>
            <span className="text-xl font-bold tracking-wide bg-white text-primary-700 px-3 py-1 rounded-md">TTS</span>
          </Link>
          {/* desktop collapse toggle */}
          <button onClick={() => setSidebarOpen(o => !o)} className="hidden md:block p-2 rounded-md hover:bg-primary-700 ml-auto" aria-label="Collapse sidebar">
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          {/* mobile close */}
          <button onClick={closeMobile} className="md:hidden p-2 rounded-md hover:bg-primary-700 ml-auto" aria-label="Close menu">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
          {NAV.map(({ to, labelKey, icon: Icon }) => (
            <NavLink key={to} to={to} onClick={closeMobile} className={navLinkCls}>
              <Icon className={`flex-shrink-0 h-5 w-5 ${iconMr}`} />
              <span className={labelCls}>{t(labelKey)}</span>
            </NavLink>
          ))}
        </nav>

        {/* History (full admins only) */}
        {fullAdmin && (
          <div className="px-2 pb-2 mt-auto">
            <NavLink to="/admin-panel/history" onClick={closeMobile} className={navLinkCls}>
              <History className={`flex-shrink-0 h-5 w-5 ${iconMr}`} />
              <span className={labelCls}>{t('history')}</span>
            </NavLink>
          </div>
        )}

        {/* User info + logout */}
        <div className={`border-t border-primary-700 ${fullAdmin ? '' : 'mt-auto'}`}>
          <div className="px-3 py-3 flex items-center bg-primary-700">
            <div className="flex-shrink-0">
              <div className="h-10 w-10 rounded-full bg-primary-600 flex items-center justify-center">
                <span className="text-sm font-bold text-white uppercase">{user?.username?.[0]}</span>
              </div>
            </div>
            <div className={`ml-3 flex-1 min-w-0 ${labelCls}`}>
              <p className="text-sm font-medium text-white truncate">{user?.username}</p>
              <p className="text-xs text-primary-200 capitalize">{user?.role}</p>
            </div>
            <button onClick={handleLogout} className="p-2 rounded-md hover:bg-primary-600 text-white ml-auto" title={t('logout')}>
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Top bar */}
        <header className="bg-white shadow-sm z-10 relative">
          <div className="px-3 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-2 text-sm text-gray-500 min-w-0">
                {/* mobile hamburger */}
                <button
                  onClick={() => setMobileOpen(true)}
                  className="md:hidden p-2 -ml-2 rounded-md text-gray-700 hover:bg-neutral-100"
                  aria-label="Open menu"
                >
                  <Menu className="h-6 w-6" />
                </button>
                <span className="font-medium text-gray-800 hidden sm:inline">Admin Panel</span>
                <ChevronRight size={14} className="hidden sm:inline flex-shrink-0" />
                <span className="text-primary-600 font-medium truncate">
                  {t(
                    NAV.find(n => window.location.pathname.startsWith(n.to))?.labelKey
                    || (window.location.pathname.startsWith('/admin-panel/history') ? 'history' : '')
                  )}
                </span>
              </div>

              {/* Language selector */}
              <div className="relative flex-shrink-0">
                <button
                  onClick={() => setShowLang(o => !o)}
                  className="flex items-center gap-1.5 px-2 sm:px-3 py-2 rounded-md text-neutral-600 hover:bg-neutral-100 transition-colors"
                >
                  <Globe className="h-5 w-5" />
                  <span className="text-sm font-medium hidden sm:inline">{LANGUAGES[language]}</span>
                  <ChevronDown className="h-4 w-4" />
                </button>
                {showLang && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowLang(false)} />
                    <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                      <div className="py-1">
                        {Object.entries(LANGUAGES).map(([code, name]) => (
                          <button
                            key={code}
                            onClick={() => { setLanguage(code); setShowLang(false); }}
                            className={`block w-full text-left px-4 py-2 text-sm hover:bg-neutral-50 ${language === code ? 'font-semibold text-primary-600' : 'text-neutral-700'}`}
                          >
                            {name}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
